import React, { useState, useCallback, useEffect } from "react";
import moment from "moment";
import {
  Card,
  Row,
  Col,
  Table,
  Space,
  Typography,
  Spin,
  Statistic,
  Button,
  Divider,
} from "antd";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useAuth0 } from "../../react-auth0-spa";
import { useAppContext } from "../../context";
import { fetchAcquistoCrediti } from "../../graphql/controllers/fetchAICredits";

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const { Title: AntTitle, Text } = Typography;

// Chart colors
const CHART_COLORS = [
  "#3D5864", // Primary brand color
  "#66A0BC",
  "#91C6E7",
  "#B6E3FF",
];

const AcquistoAddOn = ({ embedded = false }) => {
  const [loading, setLoading] = useState(false);
  const [acquisti, setAcquisti] = useState([]);
  const [dateRange, setDateRange] = useState([
    moment().subtract(3, "months").startOf("day"),
    moment().endOf("day"),
  ]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [totalPeriodCredits, setTotalPeriodCredits] = useState({
    today: 0,
    last30Days: 0,
    thisYear: 0,
    selectedPeriod: 0,
  });
  const [monthlyData, setMonthlyData] = useState({
    labels: [],
    datasets: [],
  });

  // State for the demo org inclusion checkbox
  const [includeDemoOrg, setIncludeDemoOrg] = useState(false);

  // Demo organization ID to filter out
  const DEMO_ORG_ID = "PNgeKjwe16";

  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();

  // Define handleQuickDateFilter early
  // Refined quick date filter to correctly bound "lastMonth"
  const handleQuickDateFilter = useCallback(
    (period) => {
      const now = moment();
      let startDate;
      let endDate;

      switch (period) {
        case "today":
          startDate = now.clone().startOf("day");
          endDate = now; // up to current time today
          break;
        case "currentMonth":
          startDate = now.clone().startOf("month");
          endDate = now; // up to current time this month
          break;
        case "lastMonth":
          startDate = now.clone().subtract(1, "month").startOf("month");
          endDate = now.clone().subtract(1, "month").endOf("month");
          break;
        case "30days":
          startDate = now.clone().subtract(30, "days").startOf("day");
          endDate = now;
          break;
        case "3months":
          startDate = now.clone().subtract(3, "months").startOf("day");
          endDate = now;
          break;
        case "12months":
          startDate = now.clone().subtract(12, "months").startOf("day");
          endDate = now;
          break;
        default:
          startDate = now.clone().subtract(30, "days").startOf("day");
          endDate = now;
      }

      setDateRange([startDate, endDate]);
    },
    []
  );

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getTokenSilently();

      // Fetch acquisti crediti
      const { orders } = await fetchAcquistoCrediti(
        token,
        isProd,
        10000,
        0,
        dateRange[0],
        dateRange[1]
      );

      // Filter out demo organization if needed
      let filteredOrders = [...orders];
      if (!includeDemoOrg) {
        filteredOrders = filteredOrders.filter(
          (order) => order.orgId !== DEMO_ORG_ID
        );
      }

      setAcquisti(filteredOrders);
      setPagination((prev) => ({
        ...prev,
        total: filteredOrders.length,
      }));

      // Consider only PAID orders for totals and charts
      const paidFilteredOrders = filteredOrders.filter((order) => order.isPaid === true);

      // Calculate date ranges for different periods
      const now = moment();
      const today = now.clone().startOf("day").toISOString();
      const thirtyDaysAgoIso = now
        .clone()
        .subtract(30, "days")
        .startOf("day")
        .toISOString();
      const firstDayOfYear = moment().startOf("year").toISOString();

      // Filter orders for different time periods
      const todayOrders = paidFilteredOrders.filter((order) => {
        const orderDate = new Date(order.createdAt).toISOString();
        return orderDate >= today;
      });

      const last30DaysOrders = paidFilteredOrders.filter((order) => {
        const orderDate = new Date(order.createdAt).toISOString();
        return orderDate >= thirtyDaysAgoIso;
      });

      const thisYearOrders = paidFilteredOrders.filter((order) => {
        const orderDate = new Date(order.createdAt).toISOString();
        return orderDate >= firstDayOfYear;
      });

      // Calculate total credits for each period
      const todayCredits = todayOrders.reduce(
        (sum, order) => sum + order.paidCredits,
        0
      );

      const last30DaysCredits = last30DaysOrders.reduce(
        (sum, order) => sum + order.paidCredits,
        0
      );

      const thisYearCredits = thisYearOrders.reduce(
        (sum, order) => sum + order.paidCredits,
        0
      );

      // Calculate total for selected period (all filtered orders)
      const selectedPeriodCredits = paidFilteredOrders.reduce(
        (sum, order) => sum + order.paidCredits,
        0
      );

      // Store these in state for display
      setTotalPeriodCredits({
        today: todayCredits,
        last30Days: last30DaysCredits,
        thisYear: thisYearCredits,
        selectedPeriod: selectedPeriodCredits,
      });

      // Prepare monthly data for chart (only paid orders)
      prepareChartData(paidFilteredOrders);
    } catch (error) {
      console.error("Error fetching acquisto crediti data:", error);
    } finally {
      setLoading(false);
    }
  }, [getTokenSilently, isProd, dateRange, includeDemoOrg]);

  // Initialize with last 30 days filter on component mount
  useEffect(() => {
    handleQuickDateFilter("30days");
  }, [handleQuickDateFilter]);

  // Fetch data whenever dateRange or includeDemoOrg changes
  useEffect(() => {
    fetchData();
  }, [fetchData, dateRange, includeDemoOrg]);

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  // Prepare data for chart visualization
  const prepareChartData = (orders) => {
    const monthlyAggregation = {};

    // Group orders by month
    orders.forEach(order => {
      const monthYear = moment(order.createdAt).format("MMM YYYY");
      
      if (!monthlyAggregation[monthYear]) {
        monthlyAggregation[monthYear] = {
          creditiCount: 0
        };
      }
      
      monthlyAggregation[monthYear].creditiCount += order.paidCredits;
    });

    // Sort months chronologically
    const sortedMonths = Object.keys(monthlyAggregation).sort((a, b) => {
      return moment(a, "MMM YYYY").diff(moment(b, "MMM YYYY"));
    });

    // Prepare data for chart
    const chartData = {
      labels: sortedMonths,
      datasets: [
        {
          label: 'Crediti Venduti',
          data: sortedMonths.map(month => monthlyAggregation[month].creditiCount),
          backgroundColor: `${CHART_COLORS[0]}33`, // Add transparency
          borderColor: CHART_COLORS[0],
          fill: true,
          tension: 0.4, // Smooth curve
          pointRadius: 4,
          pointBackgroundColor: CHART_COLORS[0],
          pointBorderColor: '#fff',
          pointHoverRadius: 6,
        }
      ]
    };

    setMonthlyData(chartData);
  };

  const columns = [
    {
      title: "Org",
      dataIndex: "orgName",
      key: "orgName",
      sorter: (a, b) => (a.orgName || "").localeCompare(b.orgName || ""),
      render: (orgName, record) => (
        <a href={`/orgs/${record.orgId}`} target="_blank" rel="noopener noreferrer">
          <span style={{ fontWeight: "medium" }}>
            {orgName || <span style={{ color: "#999" }}>Unknown</span>}
          </span>
        </a>
      ),
    },
    {
      title: "Data Acquisto",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      render: (date) => moment(date).format("DD/MM/YYYY HH:mm"),
    },
    {
      title: "Crediti Acquistati",
      dataIndex: "paidCredits",
      key: "paidCredits",
      sorter: (a, b) => a.paidCredits - b.paidCredits,
      render: (paidCredits) => <span>{paidCredits}</span>,
    },
    {
      title: "Riferimento Pagamento",
      dataIndex: "paymentReference",
      key: "paymentReference",
      render: (ref) => <span>{ref || 'N/A'}</span>,
    },
    {
      title: "Note",
      dataIndex: "note",
      key: "note",
      render: (note) => <span>{note}</span>,
    },
  ];

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Crediti Venduti'
        }
      },
      x: {
        title: {
          display: true,
          text: 'Mese'
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
        }
      }
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      title: {
        display: true,
        text: 'Crediti Venduti',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ${context.raw} crediti`;
          }
        }
      }
    },
  };

  // Determine the subtitle based on date range
  const getDateRangeSubtitle = () => {
    if (dateRange[0] && dateRange[1]) {
      return `Periodo: ${dateRange[0].format(
        "DD/MM/YYYY"
      )} - ${dateRange[1].format("DD/MM/YYYY")}`;
    }
    return "";
  };

  return (
    <div style={{ padding: embedded ? 0 : "20px" }}>
      {!embedded && <AntTitle level={2}>Acquisto Add-on Crediti AI</AntTitle>}

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Crediti Venduti" loading={loading}>
            <div style={{ height: 400, position: "relative" }}>
              {loading ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <Spin size="large" />
                </div>
              ) : monthlyData.labels.length > 0 ? (
                <Line options={chartOptions} data={monthlyData} />
              ) : (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <Typography.Text type="secondary">
                    Nessun dato disponibile per il periodo selezionato
                  </Typography.Text>
                </div>
              )}
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card>
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <Space direction="horizontal" size="small" wrap>
                {loading && (
                  <Spin size="small" style={{ marginRight: "8px" }} />
                )}
                <Button
                  size="small"
                  type={
                    dateRange[0] &&
                    dateRange[0].isSame(moment().startOf("day"), "day")
                      ? "primary"
                      : "default"
                  }
                  onClick={() => handleQuickDateFilter("today")}
                  loading={
                    loading &&
                    dateRange[0] &&
                    dateRange[0].isSame(moment().startOf("day"), "day")
                  }
                >
                  Oggi
                </Button>
                <Button
                  size="small"
                  type={
                    dateRange[0] &&
                    dateRange[0].isSame(moment().startOf("month"), "day")
                      ? "primary"
                      : "default"
                  }
                  onClick={() => handleQuickDateFilter("currentMonth")}
                  loading={
                    loading &&
                    dateRange[0] &&
                    dateRange[0].isSame(moment().startOf("month"), "day")
                  }
                >
                  Mese corrente
                </Button>
                <Button
                  size="small"
                  type={
                    dateRange[0] &&
                    dateRange[0].isSame(moment().subtract(1, "month").startOf("month"), "day")
                      ? "primary"
                      : "default"
                  }
                  onClick={() => handleQuickDateFilter("lastMonth")}
                  loading={
                    loading &&
                    dateRange[0] &&
                    dateRange[0].isSame(moment().subtract(1, "month").startOf("month"), "day")
                  }
                >
                  Mese scorso
                </Button>
                <Button
                  size="small"
                  type={
                    dateRange[0] &&
                    dateRange[0].isSame(
                      moment().subtract(30, "days").startOf("day"),
                      "day"
                    )
                      ? "primary"
                      : "default"
                  }
                  onClick={() => handleQuickDateFilter("30days")}
                  loading={
                    loading &&
                    dateRange[0] &&
                    dateRange[0].isSame(
                      moment().subtract(30, "days").startOf("day"),
                      "day"
                    )
                  }
                >
                  Ultimi 30 giorni
                </Button>
                <Button
                  size="small"
                  type={
                    dateRange[0] &&
                    dateRange[0].isSame(
                      moment().subtract(3, "months").startOf("day"),
                      "day"
                    )
                      ? "primary"
                      : "default"
                  }
                  onClick={() => handleQuickDateFilter("3months")}
                  loading={
                    loading &&
                    dateRange[0] &&
                    dateRange[0].isSame(
                      moment().subtract(3, "months").startOf("day"),
                      "day"
                    )
                  }
                >
                  Ultimi 3 mesi
                </Button>
                <Button
                  size="small"
                  type={
                    dateRange[0] &&
                    dateRange[0].isSame(
                      moment().subtract(12, "months").startOf("day"),
                      "day"
                    )
                      ? "primary"
                      : "default"
                  }
                  onClick={() => handleQuickDateFilter("12months")}
                  loading={
                    loading &&
                    dateRange[0] &&
                    dateRange[0].isSame(
                      moment().subtract(12, "months").startOf("day"),
                      "day"
                    )
                  }
                >
                  Ultimi 12 mesi
                </Button>
              </Space>
              <Space align="center">
                <input
                  type="checkbox"
                  id="includeDemoOrg"
                  checked={includeDemoOrg}
                  onChange={(e) => setIncludeDemoOrg(e.target.checked)}
                />
                <label htmlFor="includeDemoOrg">
                  <Typography.Text>Includi org demo</Typography.Text>
                </label>
              </Space>
            </Space>
          </Card>

          <Divider style={{ margin: "24px 0" }} />

          <Card>
            <Row gutter={[16, 16]}>
              <Col span={6}>
                <Statistic
                  title="Periodo Selezionato"
                  value={totalPeriodCredits.selectedPeriod}
                  loading={loading}
                  suffix="crediti"
                  valueStyle={{ color: '#1890ff' }}
                  formatter={value => {
                    if (value >= 1000) {
                      return `${(value / 1000).toFixed(1)}K`;
                    }
                    return value;
                  }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Oggi"
                  value={totalPeriodCredits.today}
                  loading={loading}
                  suffix="crediti"
                  formatter={value => {
                    if (value >= 1000) {
                      return `${(value / 1000).toFixed(1)}K`;
                    }
                    return value;
                  }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Ultimi 30 gg"
                  value={totalPeriodCredits.last30Days}
                  loading={loading}
                  suffix="crediti"
                  formatter={value => {
                    if (value >= 1000) {
                      return `${(value / 1000).toFixed(1)}K`;
                    }
                    return value;
                  }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Anno corrente"
                  value={totalPeriodCredits.thisYear}
                  loading={loading}
                  suffix="crediti"
                  formatter={value => {
                    if (value >= 1000) {
                      return `${(value / 1000).toFixed(1)}K`;
                    }
                    return value;
                  }}
                />
              </Col>
            </Row>
            <div style={{ marginTop: 16 }}>
              <Typography.Text type="secondary">
                Crediti venduti per periodo
              </Typography.Text>
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
        <Col span={24}>
          <Card 
            title="Dettaglio Vendite Crediti"
            extra={<Text>{getDateRangeSubtitle()}</Text>}
          >
            <Spin spinning={loading}>
              <Table
                dataSource={acquisti}
                columns={columns}
                rowKey="id"
                pagination={pagination}
                onChange={handleTableChange}
              />
            </Spin>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AcquistoAddOn;
